import React from 'react';
import { MapPin, Shield, PlusCircle, Calculator } from 'lucide-react';
import { useJobCostEstimateLaunch } from '@/contexts/JobCostEstimateLaunchContext';
import { type Office, type Department } from '@/types/org';
import { type UserProfile } from '@/types/team';
import { hasPermission } from '@/hooks/useRBAC';

interface HeaderProps {
    activeOfficeId: string | null;
    activeOffice: Office | undefined;
    activeDepartmentId: string | null;
    departments: Department[];
    profile: UserProfile | null;
    onJobCreateClick: () => void;
    /** Active org's name, the title outside an office (falls back to "Organization"). */
    organizationName?: string;
    /** Multi-org switcher, shown beside the title (renders nothing for a single-org login). */
    orgSwitcher?: React.ReactNode;
    /** WEB-100: platform-admin chip, shown in the right-hand actions group (renders nothing for non-admins). */
    adminControl?: React.ReactNode;
}

export const Header: React.FC<HeaderProps> = ({
    activeOfficeId,
    activeOffice,
    activeDepartmentId,
    departments,
    profile,
    onJobCreateClick,
    organizationName,
    orgSwitcher,
    adminControl,
}) => {
    const { launcher: openJobCostEstimate } = useJobCostEstimateLaunch();

    // flex-wrap is load-bearing: without it this row cannot fit a narrow main column and
    // trailing children (the Create Job button) are clipped away by main's
    // overflow-hidden with no way to scroll to them.
    return (
        // WEB-111 increment 4: this is the phone drawer's context block (the org or
        // office title, the org switcher, the admin chip and the actions). It used to
        // be pinned above every page below md; the phone bar (PhoneAppBar) replaced it
        // there. At md and up the top bar (TopBar) carries the same controls.
        <header className="mb-3 md:mb-4 flex flex-wrap justify-between items-center gap-3 md:gap-4 animate-in">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 md:gap-6 min-w-0">
                <div className="min-w-0 max-w-full">
                    {/* Inside an office the title is the office, so name the org above
                        it: a multi-org login otherwise cannot tell which org it is in. */}
                    {activeOfficeId && organizationName && (
                        <p className="m-0 text-micro font-bold uppercase tracking-[0.2em] text-text-secondary break-words">
                            {organizationName}
                        </p>
                    )}
                    {/* Mobile: office name is orientation, not a headline - the
                        job/page content owns the viewport (Jason, 2026-08-31). */}
                    <h2 className="text-lg md:text-xl font-black tracking-tighter m-0 gradient-text uppercase break-words leading-tight">
                        {activeOfficeId ? (activeOffice?.name || 'Office') : (organizationName || 'Organization')}
                    </h2>
                </div>

                {orgSwitcher}

                <div
                    className={`hidden lg:flex items-center gap-2 px-4 py-1.5 border rounded-full text-micro font-black uppercase tracking-[0.2em] backdrop-blur-2xl transition-all duration-500 ${activeDepartmentId
                        ? 'bg-accent-electric/10 border-accent-electric/30 text-accent-electric shadow-[0_0_20px_rgb(var(--accent-electric-rgb)/0.15)] scale-105'
                        : 'bg-accent-primary/10 border-accent-primary/30 text-accent-primary shadow-[0_0_20px_rgb(var(--accent-primary-rgb)/0.15)]'
                        }`}
                >
                    {activeOfficeId ? <MapPin size={12} strokeWidth={3} /> : <Shield size={12} strokeWidth={3} />}
                    {activeOfficeId
                        ? (activeDepartmentId
                            ? `${departments.find(d => d.id === activeDepartmentId)?.name || 'Department'} ACTIVE`
                            : 'All departments')
                        : 'All offices'}
                </div>
            </div>

            {/* Global search removed for the pilot: the input was never wired to
                anything (no handler, no hotkey), so it only manufactured
                "search is broken" reports. Restore alongside a real search. */}
            <div className="flex-1" />

            <div className="flex items-center gap-3 ml-auto">
                {adminControl}
                {openJobCostEstimate && (
                    <button
                        type="button"
                        onClick={openJobCostEstimate}
                        title="Job cost estimate"
                        className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20 transition-colors border border-emerald-500/25 hover:border-emerald-400/40"
                        aria-label="Open job cost estimate"
                    >
                        <Calculator size={18} strokeWidth={2.25} />
                    </button>
                )}
                {/* WEB-75: CREATE_JOB names who may create (not MEMBER, not FINANCE) */}
                {activeOfficeId && hasPermission(profile, 'CREATE_JOB') && (
                    <button
                        onClick={onJobCreateClick}
                        aria-label="Create job"
                        className="glass-premium flex items-center gap-2 px-3 md:px-6 py-2.5 text-text-primary border-none cursor-pointer hover-glow active:scale-95 group"
                    >
                        <PlusCircle size={18} className="text-accent-electric group-hover:rotate-90 transition-transform duration-300" />
                        <span className="hidden md:inline font-bold tracking-tighter text-sm">Create Job</span>
                    </button>
                )}
            </div>
        </header>
    );
};
